import { v4 } from "uuid";
import { useFetch, UseFetchReturn } from "@vueuse/core";
import { useGlobalStore } from "@/stores/global";
import { useAuthStore } from "@/stores/auth";
import { useCartStore } from "@/stores/cart";
import { useLineStore } from "@/stores/line";
import { LINE_LINK_HEADER } from "@/types/modules/Line";
import { createToaster } from "@meforma/vue-toaster";

interface UseGraphqlOptions {
  toasted?: boolean;
  loading?: boolean;
  errorToasted?: boolean;
}

// error.value is a transport string, never a user-facing message. On a non-2xx
// @vueuse throws new Error(Response.statusText), and statusText is EMPTY over
// HTTP/2 and HTTP/3 - the protocol carries no reason phrase - so it falls back
// to the Error *name* and reads literally "Error" behind any ALB / CloudFront /
// nginx+h2. Over HTTP/1.1 it is an English phrase. handle_error's body carries
// a raw Ruby exception message, which must not reach a customer either. So:
// one fixed Japanese sentence.
export const TRANSPORT_ERROR_MESSAGE = "通信エラーが発生しました。時間をおいて再度お試しください。";

// Internal diagnostic, never shown to a customer. Exported so a caller holding
// a rejection reason can tell "the server never answered" apart from "the
// server answered with a message worth showing".
export const NO_DATA_ERROR_MESSAGE = "GraphQL response contained no data";

export default function <T>(
  query: string,
  variables: unknown = {},
  options: UseGraphqlOptions = {
    errorToasted: true,
  },
): Promise<T> {
  const globalStore = useGlobalStore();
  const authStore = useAuthStore();
  const cartStore = useCartStore();
  const lineStore = useLineStore();
  const toast = createToaster({ position: "top-right" });

  return new Promise(async function (resolve, reject) {
    const id = v4();

    const { data, error, statusCode } = (await useFetch<T>("/graphql", {
      beforeFetch(ctx) {
        if (globalStore.keepError == false) {
          globalStore.validationErrors = {};
        }

        // Above the token check on purpose. A guest who has just finished LINE
        // authentication has a link key and no token, and they are precisely
        // the visitor the three consuming mutations (orders/create, sign_in,
        // sign_up) exist for - attaching this after the early return below
        // would mean it never went out for the only people who need it.
        if (lineStore.linkToken) {
          ctx.options.headers = {
            ...ctx.options.headers,
            [LINE_LINK_HEADER]: lineStore.linkToken,
          };
        }

        const token = authStore.token;
        if (!token) return ctx;

        ctx.options.headers = {
          ...ctx.options.headers,
          "United-Cart-Authorization": `Bearer ${token}`,
        };

        if (options.loading !== false) {
          globalStore.addRequest(id);
        }

        return ctx;
      },

      afterFetch(ctx) {
        const errors = ctx.data.errors;

        if (errors && errors.length) {
          const firstError = errors[0];
          const codeErrors = [301, 422];

          if (codeErrors.includes(firstError.code)) {
            globalStore.setValidationErrors(firstError.errors, firstError.reset_data);
          }

          // Gated like the transport-error toast below. GraphqlController
          // renders an errors array for any server exception, so this branch -
          // not just the transport one - is what a caller asking for silence
          // needs suppressed. /lp/finish asks, so that a failure here never
          // puts a red toast in front of a customer who has just paid.
          if (options.errorToasted !== false) toast.error(firstError.message);
          reject(new Error(firstError.message));

          // Return, do not fall through. A GraphQL *request* error - unknown
          // field, variable coercion failure, non-null violation - comes back
          // at HTTP 200 with "data": null or with no "data" key at all, so the
          // unwrap below throws "Cannot convert undefined or null to object"
          // inside afterFetch. useFetch catches that into error.value and the
          // transport branch then toasts it: a second toast, in English, full
          // of JS internals, for one failure. Null data settles silently via
          // the else branch at the end.
          ctx.data = null;

          return ctx;
        }

        const firstKey = Object.keys(ctx.data.data)[0];
        ctx.data = ctx.data.data[firstKey];

        return ctx;
      },
    })
      .post({ query, variables })
      .json()) as UseFetchReturn<T>;

    if (options.loading !== false) {
      globalStore.removeRequest(id);
    }

    if (error.value) {
      if (statusCode.value === 401) {
        authStore.logout();

        if (window.location.href.includes("/shop/")) {
          // The redirect IS the message. A toast here only flashes behind a
          // page that is already navigating away, on a routine session expiry.
          window.location.href = "/shop/customers/sign_in";

          return reject(new Error(TRANSPORT_ERROR_MESSAGE));
        }
      }

      // !== false, not a truthy check: the default { errorToasted: true } is
      // replaced wholesale as soon as a caller passes ANY options object, so
      // the ~20 callers passing { toasted: true } arrived here with
      // errorToasted undefined and silently swallowed transport errors - a 500
      // on payjpCardDelete showed the customer nothing at all.
      if (options.errorToasted !== false) toast.error(TRANSPORT_ERROR_MESSAGE);

      // Return: without it a non-2xx falls through to the block below, where
      // data.value is the raw error body - truthy - and resolves a promise
      // that was just rejected.
      return reject(new Error(TRANSPORT_ERROR_MESSAGE));
    }

    // Null/undefined rather than falsy: a root field returning a BaseScalar
    // can legitimately resolve to "", 0 or false, and rejecting those would
    // turn a valid response into an error.
    if (data.value !== null && data.value !== undefined) {
      resolve(data.value);

      const message = (data.value as any).message;

      if (message && options.toasted) {
        toast.success(message);
      }
    } else {
      // Reached only on a 200 with no errors array whose root field resolved
      // to null - @vueuse/core skips afterFetch on a non-2xx, so every
      // handle_error response still carries a truthy body and settles above.
      // Without this the promise settled neither way and the caller simply
      // hung. Deliberately silent: the callers that can reach it are
      // best-effort reads, and the one that strands a customer
      // (amazon_callback.vue) needs its own redirect to be unconditional -
      // a toast here would not save it.
      reject(new Error(NO_DATA_ERROR_MESSAGE));
    }
  });
}
